/**
 * iosSave — universal "save this asset" helper that gives users the best
 * native experience on each platform:
 *
 *  • iOS / iPadOS:  fetches the asset as a blob and invokes the Web Share
 *    Sheet with a File — surfaces the native "Save Image…" (Photos) or
 *    "Save to Files / Save to Drive" picker that the user expects when
 *    long-pressing media on the web.
 *
 *  • Android / Desktop / fallback: opens the asset in a new tab with
 *    `download`. The browser then either saves immediately or shows the
 *    OS save dialog.
 *
 * Safe to call multiple times — always returns a Promise that resolves
 * when the action sheet closes (or the new tab opens).
 *
 * Usage
 *   import { iosSave } from "../lib/iosSave";
 *   await iosSave(url, { filename: "creative.mp4", kind: "video" });
 */

const _isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPad on iOS 13+ reports as Mac with touch — fold it in.
  return /iPad|iPhone|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && "ontouchend" in document);
};

const _guessMime = (url, kind) => {
  if (kind === "image") return "image/jpeg";
  if (kind === "video") return "video/mp4";
  const ext = (url.split("?")[0].split(".").pop() || "").toLowerCase();
  const map = {
    jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png",
    webp: "image/webp", gif: "image/gif", heic: "image/heic",
    mp4: "video/mp4", mov: "video/quicktime", webm: "video/webm",
    mp3: "audio/mpeg", wav: "audio/wav", m4a: "audio/mp4",
  };
  return map[ext] || (kind === "image" ? "image/jpeg" : "application/octet-stream");
};

const _fallbackNewTab = (url) => {
  // Last-resort fallback. Open the asset in a new tab so the user can
  // long-press (mobile) or use Cmd-S / right-click (desktop) to save.
  try {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.download = "";   // hint download intent; desktop browsers honour it
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (_e) {
    window.open(url, "_blank", "noopener,noreferrer");
  }
};

export async function iosSave(url, { filename, kind = "video" } = {}) {
  if (!url) return;
  const safeName = filename || `xea-${kind}-${Date.now()}.${kind === "image" ? "jpg" : "mp4"}`;

  // On iOS, prefer the Web Share API with a File so the user gets the
  // native "Save Image / Save to Files / Save to Drive" action sheet.
  if (_isIOS() && typeof navigator !== "undefined" && navigator.share) {
    try {
      const res = await fetch(url, { credentials: "omit" });
      if (!res.ok) throw new Error(`download failed: ${res.status}`);
      const blob = await res.blob();
      const mime = blob.type || _guessMime(url, kind);
      const file = new File([blob], safeName, { type: mime });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files:   [file],
          title:   safeName,
          text:    "Saved from XEA",
        });
        return;
      }
    } catch (_e) {
      /* fall through to new-tab fallback */
    }
  }

  _fallbackNewTab(url);
}
